import knowledgeImg from "../assets/img/Knowledge observatory.png";
import linearImg from "../assets/img/Programming Final Presentation.jpg";
import sdeQueueImg from "../assets/img/Second Review (1).png";
import sdeExtractionImg from "../assets/img/Epidemiological Model Reproducibility.png";
import tapestryImg from "../assets/img/tapestry.jpg";
import lensImg from "../assets/img/observatory-decomposition.jpg";
import ssepImg from "../assets/img/Biosensors.png";
import signalStructureImg from "../assets/img/signal-and-structure.jpg";
import humanCenSysImg from "../assets/img/humancensys.jpg";
import framesAppImg from "../assets/img/Research Dashboard.jpg";
import { nodes } from "./decomposition";

// URLs come from the decomposition dataset (src/data/decomposition.js), the one
// source of truth shared with the plate at "/". This file keeps only
// presentation: image, tagline, description, featured, ordering, people.
//
// Every card names its node (`nodeId`) and says where each URL field comes
// from (`urls`). A source is:
//   "live" | "repo" | "dist"      that link on the card's own node
//   ["otherNodeId", "live"]       that link on a different node
//   { local: "https://..." }      a URL the dataset has no slot for yet
//   null                          no link
// `provenance` records which, so src/data/parity.test.js can check it.
//
// A `priv` node never yields a repo URL, whatever the dataset holds.
const cards = [
  {
    slug: "tapestry",
    nodeId: "tapestry",
    urls: {
      // The dataset's live link is the documentation root; the app lives at
      // /home, which the dataset has no slot for.
      siteUrl: { local: "https://tapestry-khaki.vercel.app/home" },
      repoUrl: "repo",
      scienceUrl: "live",
      packageUrl: ["tapestry-cli", "dist"],
    },
    title: "Tapestry",
    tagline: "Project intelligence for AI-native teams.",
    description:
      "Tapestry helps operators accumulate capability instead of rebuilding context — observe project evolution, preserve knowledge, discover patterns, and turn repeated work into durable structure.",
    image: tapestryImg,
    scienceLabel: "The Documentation",
    packageLabel: "The Package (PyPI)",
    // Shown on the featured card, verbatim from the repo README. Display
    // only: the card is a link, so these are not copy buttons. The first is
    // a shell command; the rest are Claude Code slash commands.
    install: [
      "pipx install tapestry-cli",
      "/plugin marketplace add Lizo-RoadTown/tapestry",
      "/plugin install tapestry-discipline@tapestry",
      "/plugin install tapestry-patterns@tapestry",
    ],
    collaborators: [],
    advisors: [],
    featured: true,
  },
  {
    slug: "the-lens",
    nodeId: "lens-core",
    urls: {
      repoUrl: "repo",
    },
    title: "The Lens",
    description:
      "Customizable modular software for knowledge mapping of physical components in sociotechnical systems.",
    image: lensImg,
    // The modular units, one repo each. Purposes are the repos' own
    // descriptions, trimmed. Names are not links: the whole card is already a
    // link, and nesting anchors is invalid.
    modules: [
      { nodeId: "lens-core", name: "core", purpose: "shared database shape and module composition" },
      { nodeId: "lens-ingest", name: "ingest", purpose: "source material into staged candidate records" },
      { nodeId: "lens-review", name: "review", purpose: "accept, reject, edit; promote to the verified library" },
      { nodeId: "lens-observe", name: "observe", purpose: "compute signals over activity" },
      { nodeId: "lens-serve", name: "serve", purpose: "query the verified library via API and MCP" },
    ],
    collaborators: [],
    advisors: [],
    featured: true,
  },
  {
    slug: "sde-extraction",
    nodeId: "sde-extraction",
    urls: {
      siteUrl: "live",
      repoUrl: "repo",
      scienceUrl: { local: "https://sde-extraction-jiow.vercel.app/" },
    },
    title: "Reproducibility Auto-Check for SDE Models",
    tagline: "Automated reproducibility verification for stochastic differential equation models in published research.",
    description:
      "An automated reproducibility checker for stochastic differential equation (SDE) models in published research — built to support reproducibility work in systems medicine and epidemiological modeling. The live site is the auto-check interface; the documentation site walks through how the system parses, validates, and surfaces SDE parameters from manuscripts.",
    image: sdeExtractionImg,
    scienceLabel: "The Documentation",
    collaborators: [],
    advisors: [
      { name: "Dr. TJ Sego", role: "University of Florida — Laboratory of Systems Medicine", website: "https://systemsmedicine.pulmonary.medicine.ufl.edu/student-mentorship-program/dr-t-j-sego-research/" },
    ],
  },
  {
    slug: "knowledge-observatory",
    nodeId: "Knowledge Observatory",
    urls: {
      // The card links the instrument's dashboard and library, which are
      // their own nodes; the docs site is this node's own live link.
      siteUrl: ["proves-curation-dashboard", "live"],
      repoUrl: ["PROVES_LIBRARY", "repo"],
      scienceUrl: "live",
    },
    title: "Knowledge Observatory",
    tagline: "A knowledge observation instrument for engineering systems — making the invisible structure of organizational knowledge visible.",
    description:
      "An AI-assisted instrument for mapping organizational knowledge onto physical components in sociotechnical systems. Built on a multi-agent extraction pipeline and a verified knowledge graph — designed to detect knowledge loss as it happens in fast-moving CubeSat programs.",
    image: knowledgeImg,
    scienceLabel: "The Documentation",
    collaborators: [],
    advisors: [],
  },
  {
    slug: "ssep",
    nodeId: "amp-microgravity-site",
    urls: {
      siteUrl: "live",
      // The dataset lists a repo for this node; this card has never shown one.
      // Withheld, not removed: see KNOWN_WITHHELD in parity.test.js.
      repoUrl: null,
      scienceUrl: { local: "http://ssep.ncesse.org/communities/experiments-selected-for-flight/selected-experiments-on-ssep-mission-21-to-iss/#:~:text=California%20State%20Polytechnic%20University%2C%20Pomona" },
    },
    title: "AMP Biosensor in Microgravity",
    tagline: "Microgravity biosensor experiment design — Honorable Mention, NCESSE SSEP Mission 21 to ISS.",
    description:
      "A microgravity biosensor experiment designed to test how reduced gravity affects bacterial attachment to antimicrobial-peptide coated surfaces. Reviewed for flight to the International Space Station as a Mission 21 finalist — findings will inform spacecraft biosensor and surface coating design.",
    image: ssepImg,
    collaborators: [
      { name: "Frank Puga-Raya", role: "AT³ / Pathways to STEM Student Cohort", website: "https://www.linkedin.com/in/frank-puga-raya-895568283/" },
      { name: "Steven Picazo", role: "AT³ / Pathways to STEM Student Cohort", website: "https://www.linkedin.com/in/steven-picazo-994042225/" },
      { name: "Jesus Coca", role: "AT³ / Pathways to STEM Student Cohort", website: "https://www.linkedin.com/in/jesus-coca-706191258/" },
      { name: "Alejandro Lopez", role: "AT³ / Pathways to STEM Student Cohort", website: "https://www.linkedin.com/in/alejandro-lopez-lepe/" },
      { name: "Damian Palacios-Rosas", role: "AT³ / Pathways to STEM Student Cohort", website: "https://www.linkedin.com/in/damian-palacios-rosas-44767b257/" },
      { name: "Maya Ramirez", role: "AT³ / Pathways to STEM Student Cohort", website: "https://www.linkedin.com/in/maya-evelia-ramirez/" },
    ],
    advisors: [
      { name: "Michael Pham", role: "Bronco Space Lab / Cal Poly Pomona", website: "https://www.linkedin.com/in/michael-pham-04743a161/" },
      { name: "Dr. Chris Buser", role: "University of Bridgeport, BioCubic", website: "https://www.linkedin.com/in/chris-buser-phd/" },
      { name: "Dr. Jared Ashcroft", role: "AT³ / Pasadena City College", website: "https://www.linkedin.com/in/jared-ashcroft-44801a87/" },
    ],
  },
  {
    slug: "sde-review-queue",
    nodeId: "file_queuing_system",
    urls: {
      siteUrl: null,
      repoUrl: "repo",
      scienceUrl: { local: "https://systemsmedicine.pulmonary.medicine.ufl.edu/" },
    },
    title: "SDE Research Review Queue",
    tagline: "A system for coordinating second reviews of research manuscripts and Jupyter notebooks.",
    description:
      "A coordination system for second reviews of research manuscripts and Jupyter notebooks, built entirely on GitHub. Tracks every checkout, diff, dispute, and decision automatically — so reviewers and curators can work asynchronously with full transparency.",
    image: sdeQueueImg,
    collaborators: [
      { name: "Louise Marie Maganto", role: "AT³ Student Cohort", website: "https://www.linkedin.com/in/louise-marie-maganto-958410205/" },
      { name: "Jamaal Porchia", role: "AT³ Student Cohort", website: "https://www.linkedin.com/in/jamaal-porchia-b0b744304/" },
      { name: "Nhu Huynh", role: "AT³ Student Cohort", website: "https://www.linkedin.com/in/nhu-hue-huynh-ba3197254/" },
    ],
    advisors: [
      { name: "Dr. TJ Sego", role: "University of Florida — Laboratory of Systems Medicine", website: "https://systemsmedicine.pulmonary.medicine.ufl.edu/student-mentorship-program/dr-t-j-sego-research/" },
    ],
  },
  {
    slug: "linear-programming",
    nodeId: "Interactive LP Tool",
    urls: {
      siteUrl: "live",
      repoUrl: "repo",
      scienceUrl: { local: "http://purplemath.com/modules/linprog.htm" },
    },
    title: "Linear Programming Learning Tool",
    tagline: "Interactive teaching tool for visualizing and solving linear programming problems.",
    description:
      "An interactive web-based tool for visualizing and solving linear programming problems. Developed alongside coursework in Industrial and Manufacturing Engineering at Cal Poly Pomona, with an underlying optimization model built for 787 Dreamliner profit maximization.",
    image: linearImg,
    collaborators: [
      { name: "E.H.", role: "Cal Poly Pomona Student Cohort" },
      { name: "Jordan Van", role: "Cal Poly Pomona Student Cohort", website: "https://www.linkedin.com/in/jordan-van-0507b2249/" },
      { name: "Michael Ramirez", role: "Cal Poly Pomona Student Cohort", website: "https://www.linkedin.com/in/michael-ramirez-9489a1323/" },
      { name: "Miguel Magana", role: "Cal Poly Pomona Student Cohort", website: "https://www.linkedin.com/in/miguel-magana-cpp/" },
    ],
    advisors: [
      { name: "Dr. Javad Seif", role: "Cal Poly Pomona" },
    ],
    openForCollab: true,
  },
  {
    slug: "signal-and-structure",
    nodeId: "signal-and-structure",
    urls: {
      siteUrl: null,
      repoUrl: "repo",
      scienceUrl: null,
    },
    title: "Signal & Structure",
    description:
      "NASA Space Apps Challenge project — Signal and Structure (web + server + Python engine).",
    image: signalStructureImg,
    collaborators: [],
    advisors: [],
  },
  {
    slug: "human-centered-systems",
    nodeId: "Human Centered Systems",
    urls: {
      // Closed source: the node is priv, so no repo URL can resolve.
      siteUrl: "live",
      repoUrl: "repo",
      scienceUrl: null,
    },
    title: "Human Centered Systems, LLC",
    tagline: "R&D for Complex Systems",
    image: humanCenSysImg,
    collaborators: [],
    advisors: [],
  },
  {
    slug: "university-research-dashboard",
    nodeId: null,
    urls: {
      // No node in the dataset for this project. Reported, not invented.
      siteUrl: { local: "https://eosborn.pythonanywhere.com/" },
      repoUrl: { local: "https://github.com/Lizo-RoadTown/Old-Frames-App" },
      scienceUrl: null,
    },
    title: "University Research Dashboard",
    tagline: "An early prototype dashboard for surfacing university research activity and outputs.",
    description:
      "A dashboard for visualizing research activity across a university — built as the original FRAMES prototype, before the structural diagnostic work split into its own research direction.",
    image: framesAppImg,
    collaborators: [],
    advisors: [
      { name: "Michael Pham", role: "Bronco Space Lab / Cal Poly Pomona", website: "https://www.linkedin.com/in/michael-pham-04743a161/" },
    ],
  },
];

const byId = new Map(nodes.map((n) => [n.id, n]));
const URL_FIELDS = ["siteUrl", "repoUrl", "scienceUrl", "packageUrl"];

// Resolve one source against the dataset. A priv node's repo is never
// returned: priv means the source is closed.
const resolve = (cardNodeId, source) => {
  if (source === null || source === undefined) return { url: null, from: null };
  if (typeof source === "object" && !Array.isArray(source)) {
    return { url: source.local, from: "local" };
  }
  const [nodeId, link] = Array.isArray(source) ? source : [cardNodeId, source];
  const n = byId.get(nodeId);
  if (!n) throw new Error(`projects.js: no node "${nodeId}" in decomposition.js`);
  if (link === "repo" && n.priv) return { url: null, from: null };
  const url = (n.links && n.links[link]) || null;
  return { url, from: url ? { node: nodeId, link } : null };
};

export const projects = cards.map(({ urls, ...card }) => {
  const out = { ...card, provenance: {} };
  URL_FIELDS.forEach((f) => {
    const { url, from } = resolve(card.nodeId, urls[f]);
    out[f] = url;
    if (from) out.provenance[f] = from;
  });
  return out;
});

export const getProjectBySlug = (slug) => projects.find((p) => p.slug === slug);
